import React, { useState, useEffect } from 'react';
import { Popover, Box, Typography, CircularProgress, Alert, useTheme, Paper } from '@mui/material';
import { Visibility } from '@mui/icons-material';
import fetchData from '../../helpers/fetchData';
import { tokens } from '../../theme';

interface AdPreviewPopoverProps {
  open: boolean;
  anchorPosition?: { top: number; left: number };
  materialId: number | null;
  productName?: string;
  onClose: () => void;
}

const AdPreviewPopover: React.FC<AdPreviewPopoverProps> = ({
  open,
  anchorPosition,
  materialId,
  productName,
  onClose,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [loading, setLoading] = useState(true);
  const [previewHtml, setPreviewHtml] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [previewCache, setPreviewCache] = useState<{ [key: number]: string }>({});

  // Clear cache when component unmounts to prevent memory leaks
  useEffect(() => {
    return () => {
      setPreviewCache({});
    };
  }, []);

  useEffect(() => {
    if (open && materialId) {
      fetchPreview();
    }
  }, [open, materialId]);

  const fetchPreview = async () => {
    if (!materialId) return;

    // Check cache first
    if (previewCache[materialId]) {
      setPreviewHtml(previewCache[materialId]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = (await fetchData(
        `/api/ad-statistics/preview/${materialId}`,
        {},
        false,
      )) as any;

      if (response.success && response.html) {
        setPreviewHtml(response.html);
        // Cache the preview
        setPreviewCache((prev) => ({ ...prev, [materialId]: response.html }));
      } else {
        setError('Vorschau nicht verfügbar');
      }
    } catch (err: any) {
      console.error('Error fetching preview:', err);
      if (err.message?.includes('404')) {
        setError('Material nicht gefunden');
      } else if (err.message?.includes('408')) {
        setError('Zeitüberschreitung');
      } else {
        setError('Vorschau nicht verfügbar');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPreviewHtml('');
    setError(null);
    onClose();
  };

  if (!open || !materialId) return null;

  return (
    <Popover
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      onClose={handleClose}
      sx={{
        pointerEvents: 'none',
        '& .MuiPopover-paper': {
          pointerEvents: 'auto',
          maxWidth: '500px',
          maxHeight: '400px',
          overflow: 'auto',
          boxShadow: 3,
        },
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      disableRestoreFocus
      disableAutoFocus
      disableEnforceFocus
    >
      <Paper
        sx={{
          p: 2,
          backgroundColor: colors.primary[400],
          border: `1px solid ${colors.grey[700]}`,
        }}
      >
        {/* Header */}
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Visibility fontSize="small" />
          <Typography variant="subtitle2" noWrap>
            Material {materialId}
            {productName && ` - ${productName}`}
          </Typography>
        </Box>

        {/* Content */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ fontSize: '0.75rem' }}>
            {error}
          </Alert>
        ) : (
          <Box
            sx={{
              '& img': {
                maxWidth: '100%',
                height: 'auto',
              },
              '& a': {
                color: 'primary.main',
                textDecoration: 'underline',
              },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.75rem',
              },
              '& td, & th': {
                padding: '4px',
                textAlign: 'left',
                fontSize: '0.75rem',
              },
              // Style for SAM metadata box
              '& div[style*="background: #f0f0f0"]': {
                backgroundColor: colors.grey[700],
                color: colors.grey[100],
                borderColor: colors.grey[600],
              },
              // General text styling
              color: colors.grey[100],
              fontSize: '0.75rem',
              '& p, & div, & span': {
                color: 'inherit',
                fontSize: '0.75rem',
              },
              // Limit height and add scroll
              maxHeight: '300px',
              overflow: 'auto',
            }}
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        )}
      </Paper>
    </Popover>
  );
};

export default AdPreviewPopover;
